import { Alert, Tab, Tabs, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PriorityActionBanner from '../components/Dashboard/PriorityActionBanner';
import TenderTable from '../components/Dashboard/TenderTable';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useDashboardMetadata } from '../hooks/useDashboardMetadata';
import { useTenders } from '../hooks/useTenders';
import { useUserActions } from '../hooks/useUserActions';
import { apiService } from '../services/api';
import type { Tender } from '../types/tender';
import { logTenderActivity } from '../utils/activityLogger';
import { filterVisibleTenders } from '../utils/dashboardUtils';
import { removeRecentTender } from '../utils/recentTenders';

const DashboardPage = () => {
  const { isLoading: isLoadingAuth } = useAuth();
  const { account, hasMissingAccountInfo } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [setupError, setSetupError] = useState('');

  // Custom Hooks
  const { tenders, isLoadingTenders, tendersError } = useTenders();
  const { userActions } = useUserActions();

  // Mapping for filters
  const { usersMap, teamsMap } = useDashboardMetadata(account);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!isLoadingAuth) {
      if (account) {
        // AuthContext has provided account
      } else if (hasMissingAccountInfo) {
        setSetupError('User account setup is incomplete. Please contact support.');
      } else if (!account && !hasMissingAccountInfo) {
        // Waiting for account or missing info flag from AuthContext
      }
    }
  }, [account, isLoadingAuth, hasMissingAccountInfo]);

  // Handlers
  const handleArchiveTender = async (tenderId: string, reason: string) => {
    if (!tenderId || !reason) {
      return;
    }
    try {
      await apiService.archiveTender(tenderId, reason);
      if (account?.uid) {
        removeRecentTender(account.uid, tenderId);
      }
      logTenderActivity.updated({
        id: tenderId,
        updateType: `archived: ${reason}`,
      });
    } catch (_error: unknown) {
      showSnackbar('Failed to archive tender. Please try again.', 'error');
    }
  };

  // Filter tenders for Team Leads
  const visibleTenders = useMemo(
    () => filterVisibleTenders(tenders, account, usersMap),
    [tenders, account, usersMap]
  );

  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'pursuing' | 'watching'>(
    (location.state as { tab?: string })?.tab === 'watching' ? 'watching' : 'pursuing'
  );

  // Filter based on activeTab
  const filteredTabTenders = useMemo(() => {
    return visibleTenders.filter((t) => {
      if (activeTab === 'watching') {
        return t.pursuitStatus === 'watching';
      }
      // 'pursuing' includes undefined for backward compatibility
      return !t.pursuitStatus || t.pursuitStatus === 'pursuing';
    });
  }, [visibleTenders, activeTab]);

  // State for stats cards (reflects table filters)
  const [_displayedTenders, setDisplayedTenders] = useState<Tender[]>([]);

  // Update displayed tenders when base data changes
  useEffect(() => {
    setDisplayedTenders(filteredTabTenders);
  }, [filteredTabTenders]);

  if (isLoadingAuth) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress
          sx={{ color: theme.palette.bidlogiq?.orange || '#FFA500' }}
          size={60}
          thickness={4}
        />
        <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.primary }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (hasMissingAccountInfo && !account) {
    return (
      <PageContainer sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Account Not Configured
        </Typography>
        <Typography gutterBottom>
          Your account is not fully set up. If you have just signed up, the system is configuring
          your account. Try refreshing in a moment.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{
            backgroundColor: theme.palette.bidlogiq?.rust,
            color: theme.palette.common.white,
            mt: 2,
          }}
        >
          Refresh Page
        </Button>
        {setupError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {setupError}
          </Typography>
        )}
      </PageContainer>
    );
  }

  if (!account) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress
          sx={{ color: theme.palette.bidlogiq?.orange || '#FFA500' }}
          size={60}
          thickness={4}
        />
        <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.primary }}>
          Loading dashboard setup...
        </Typography>
      </Box>
    );
  }

  return (
    <PageContainer data-testid="dashboard" title="" maxWidth={false}>
      {isSmallScreen && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            backgroundColor: 'bidlogiq.rust',
            color: 'common.white',
            '& .MuiAlert-icon': {
              color: 'common.white',
            },
          }}
        >
          On mobile devices, generated Word documents can only be viewed in the official (free)
          Microsoft Word app using "Print View".
        </Alert>
      )}

      {/* Custom Header - REMOVED */}

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 4, pt: 2 }}>
        {/* Tabs for Active Pursuits vs Go/No-Go Gate */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, val) => setActiveTab(val)}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: (theme) => theme.palette.bidlogiq.brick,
              },
            }}
          >
            <Tab
              label={`Active Pursuits (${visibleTenders.filter((t) => !t.pursuitStatus || t.pursuitStatus === 'pursuing').length})`}
              value="pursuing"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&.Mui-selected': { color: (theme) => theme.palette.bidlogiq.brick },
              }}
            />
            <Tab
              label={`Go/No-Go (${visibleTenders.filter((t) => t.pursuitStatus === 'watching').length})`}
              value="watching"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&.Mui-selected': { color: (theme) => theme.palette.bidlogiq.brick },
              }}
            />
          </Tabs>
        </Box>

        {/* Section 1: Stats (Temporarily Disabled) */}
        {/* <DashboardStatsCards tenders={displayedTenders} /> */}

        {/* Section 2: Priority Actions */}
        {activeTab === 'pursuing' && (
          <PriorityActionBanner tenders={tenders} userActions={userActions} />
        )}

        {/* Section 3: Active Tenders Table */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
          }}
        >
          <TenderTable
            rows={filteredTabTenders} // Pass filtered state
            isLoading={isLoadingTenders}
            error={tendersError}
            onFetchTenders={async () => {
              // No-op, managed by listener
            }}
            onArchiveTender={handleArchiveTender}
            onUploadNewDraftForRow={() => {
              // Deprecated or moved logic
            }}
            onToggleReadStatus={() => {
              // Read status handled elsewhere
            }}
            showDropzoneSelectButton={false} // Hidden in this view
            usersMap={usersMap}
            teamsMap={teamsMap}
            onViewChange={setDisplayedTenders}
          />
        </Box>
      </Box>
    </PageContainer>
  );
};

export default DashboardPage;
